import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, map, filter } from 'rxjs/operators';
import { UserCredentials, AuthenticateReturn, MfaVerifyRequest, MfaVerifyResponse, CollectionGet, CollectionCreate, CollectionBase } from '../models';
import { ProjectGet } from '../models/project/project-get.model';
import { ProjectCreate } from '../models/project/project-create.model';
import { ProjectBase } from '../models/project/project-base.model';
import { UnifiedActivityItem } from '../models/project/unified-activity-item.model';
import { SuggestColumnsRequest, SuggestColumnsResponse } from '../models/project';
import { DataSourceCreate, DataSourceUpdate, DataSourceGet, DataSourceRegister, FileUploadRequest, FileUploadReplaceRequest, FileUploadUpdateResult, FileUploadSchemaRequest, FileUploadSchemaResponse } from '../models/datasource';
import { DataDestinationAddRequest, DataDestinationGet } from '../models/data-destination';
import { DataProfileResponse, DataProfileGet, DataProfileListItem } from '../models/data-profile';
import { GoalGet, GoalCreate, GoalUpdate, GoalCatalogGet } from '../models/goal';
import { TaskGet, StarterTasksResponse, TaskStarter, TaskCatalogGet } from '../models/task';
import { FavoriteTaskRequest } from '../models/task/favorite-task-request.model';
import { FavoriteTaskResponse } from '../models/task/favorite-task-response.model';
import { OutputGet } from '../models/output';
import { AdMethod, AdMethodCatalogGet, AnomalyDetectionRequest, AnomalyDetectionDatasourceRequest, AnomalyDetectionResponse, AnomalyDetectionRunGet, AnomalyDetectionRunListItem } from '../models/anomaly-detection';
import { HypothesisTestingRequest, HypothesisTestingRunGet, HtMethodCatalogGet } from '../models/hypothesis-testing';
import { LongRunningActionStatus, ActionResultGet } from '../models/long-running-action';
import { UserPlan, AvailablePlan } from '../models/plan';
import { MarketplaceResolveRequest, MarketplaceResolveResponse, MarketplaceActivateRequest, MarketplaceActivateResponse } from '../models/marketplace';
import { UserSettings, PasswordResetCodeRequest, PasswordResetCodeValidateRequest, PasswordResetRequest, PasswordChangeRequest, SubscriptionUserListItem, SubscribeUserRequest } from '../models/user';
import { UsageSummaryResponse } from '../models/usage';
import { 
  EmailVerifyRequest, EmailVerifyResponse, EmailVerifyCodeValidateRequest, EmailVerifyCodeValidateResponse,
  UserAddRequest, UserAddResponse, RegistrationUserUpdate, UserUpdateResponse,
  CompanyAdd, CompanyCreateResponse, SubscriptionCreate, SubscriptionResponse,
  Region, Industry, Country, RegistrationAvailablePlan
} from '../models/registration';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment.development';

export interface ProfilingStepOption {
  id: string;
  label: string;
  simpleDescription: string;
  advancedDescription: string;
  isDefault: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  // API URL is now managed via environment files
  private readonly baseUrl = environment.apiUrl;
  
  
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  };

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  /**
   * Authenticate user with credentials
   * @param credentials User credentials for authentication
   * @returns Observable<AuthenticateReturn>
   */
  authenticate(credentials: UserCredentials): Observable<AuthenticateReturn> {
    const url = `${this.baseUrl}/Authenticate`;
    return this.http.post<AuthenticateReturn>(url, credentials, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Verify MFA code
   * @param request MFA verification request
   * @returns Observable<MfaVerifyResponse>
   */
  mfaVerify(request: MfaVerifyRequest): Observable<MfaVerifyResponse> {
    const url = `${this.baseUrl}/Authenticate/mfa-verify`;
    return this.http.post<MfaVerifyResponse>(url, request, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Request password reset code to be sent to user's email
   * @param userName User's email/username
   * @returns Observable<boolean> - Returns true if code was sent successfully
   */
  requestPasswordResetCode(userName: string): Observable<boolean> {
    const url = `${this.baseUrl}/Authenticate/password-reset-code`;
    const request: PasswordResetCodeRequest = { product: 'DQP', userName };
    return this.http.post<boolean>(url, request, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Validate password reset code
   * @param code Reset code to validate
   * @param userName User's email/username
   * @returns Observable<boolean> - Returns true if code is valid
   */
  validatePasswordResetCode(code: string, userName: string): Observable<boolean> {
    const url = `${this.baseUrl}/Authenticate/password-reset-code-validate`;
    const request: PasswordResetCodeValidateRequest = { code, userName };
    return this.http.post<boolean>(url, request, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Reset password using reset code
   * @param code Reset code
   * @param newPassword New password
   * @param userName User's email/username
   * @returns Observable<boolean> - Returns true if password was reset successfully
   */
  resetPassword(code: string, newPassword: string, userName: string): Observable<boolean> {
    const url = `${this.baseUrl}/Authenticate/password-reset`;
    const request: PasswordResetRequest = { code, newPassword, userName };
    return this.http.post<boolean>(url, request, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Change password for authenticated user
   * @param currentPassword Current password
   * @param newPassword New password
   * @returns Observable<boolean> - Returns true if password was changed successfully
   */
  changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
    const url = `${this.baseUrl}/User/change-password`;
    const request: PasswordChangeRequest = { currentPassword, newPassword };
    return this.http.post<boolean>(url, request, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Logout user
   * @returns Observable<boolean> - Returns true if logout was successful
   */
  logout(): Observable<boolean> {
    const url = `${this.baseUrl}/User/logout`;
    return this.http.post<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  // =============================================
  // REGISTRATION API METHODS
  // =============================================

  /**
   * Send verification code to user's email
   * @param email User's email address
   * @returns Observable<{ success: boolean; message?: string }>
   */
  sendVerificationCode(email: string): Observable<{ success: boolean; message?: string }> {
    const request: EmailVerifyRequest = {
      UserName: email
    };
    
    return this.http.post<any>(`${this.baseUrl}/UserRegister/email-verify`, request, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError),
      map(response => {
        const returnCode = response.returnCode || response.ReturnCode;
        const message = response.message || response.Message;
        
        if (returnCode === 1002) {
          return { success: true, message: message || 'Verification code sent' };
        }
        throw new Error(message || 'Failed to send verification code');
      })
    );
  }

  /**
   * Verify email code
   * @param email User's email address
   * @param code 6-digit verification code
   * @returns Observable<{ valid: boolean; message?: string }>
   */
  verifyEmailCode(email: string, code: string): Observable<{ valid: boolean; message?: string }> {
    const request: EmailVerifyCodeValidateRequest = {
      UserName: email,
      Code: code,
      Product: 'DQP'
    };
    
    return this.http.post<any>(`${this.baseUrl}/UserRegister/email_verify-code-validate`, request, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError),
      map(response => ({
        valid: response.success ?? response.Success ?? false,
        message: response.message || response.Message
      }))
    );
  }

  /**
   * Register new user
   * @param email User's email address
   * @param password User's password
   * @returns Observable<UserAddResponse>
   */
  registerUser(email: string, password: string): Observable<UserAddResponse> {
    const request: UserAddRequest = {
      UserName: email,
      Password: password,
      UserTypeId: 2
    };

    return this.http.post<any>(`${this.baseUrl}/UserRegister/register`, request, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError),
      map(response => ({
        Success: response.success ?? response.Success ?? false,
        Message: response.message || response.Message || '',
        UserId: response.userId || response.UserId,
        UserName: response.userName || response.UserName
      }))
    );
  }

  /**
   * Update user profile information
   * @param userData User data to update
   * @returns Observable<UserUpdateResponse>
   */
  updateUserProfile(userData: RegistrationUserUpdate): Observable<UserUpdateResponse> {
    return this.http.put<any>(`${this.baseUrl}/user/update`, userData, this.getAuthHttpOptions()).pipe(
      retry(1),
      catchError(this.handleError),
      map(response => ({
        success: response.success ?? response.Success ?? false,
        message: response.message || response.Message || ''
      }))
    );
  }

  /**
   * Create company
   * @param companyData Company data to create
   * @returns Observable<{ success: boolean; companyId?: string; message?: string }>
   */
  createCompany(companyData: CompanyAdd): Observable<{ success: boolean; companyId?: string; message?: string }> {
    return this.http.post<any>(`${this.baseUrl}/Company`, companyData, this.getAuthHttpOptions()).pipe(
      retry(1),
      catchError(this.handleError),
      map(response => ({
        success: response.success ?? response.Success ?? false,
        message: response.message || response.Message || '',
        companyId: response.companyId || response.CompanyId
      }))
    );
  }

  /**
   * Subscribe to plan
   * @param subscriptionData Subscription data
   * @returns Observable<SubscriptionResponse>
   */
  subscribeToPlan(subscriptionData: SubscriptionCreate): Observable<SubscriptionResponse> {
    return this.http.post<any>(`${this.baseUrl}/Company/subscribe`, subscriptionData, this.getAuthHttpOptions()).pipe(
      retry(1),
      catchError(error => {
        console.error('Failed to subscribe to plan:', error);
        throw error;
      }),
      map(response => ({
        success: response.success ?? response.Success ?? false,
        message: response.message || response.Message || '',
        subscriptionId: response.subscriptionId || response.SubscriptionId
      }))
    );
  }

  /**
   * Get regions lookup data
   * @returns Observable<Region[]>
   */
  getRegions(): Observable<Region[]> {
    return this.http.get<Region[]>(`${this.baseUrl}/Lookup/regions`, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get industries lookup data
   * @returns Observable<Industry[]>
   */
  getIndustries(): Observable<Industry[]> {
    return this.http.get<Industry[]>(`${this.baseUrl}/Lookup/industries`, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get countries lookup data
   * @returns Observable<Country[]>
   */
  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${this.baseUrl}/Lookup/countries`, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get available plans for a region (registration flow)
   * @param regionCode Optional region code to filter plans
   * @returns Observable<RegistrationAvailablePlan[]>
   */
  getRegistrationAvailablePlans(regionCode?: string): Observable<RegistrationAvailablePlan[]> {
    const url = regionCode 
      ? `${this.baseUrl}/User/available-plans?region=${regionCode}`
      : `${this.baseUrl}/User/available-plans`;
    
    return this.http.get<RegistrationAvailablePlan[]>(url, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get usage summary for the current user's plan
   */
  getUsageSummary(): Observable<UsageSummaryResponse> {
    const url = `${this.baseUrl}/Usage/Summary`;
    return this.http.get<UsageSummaryResponse>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // =============================================
  // END REGISTRATION API METHODS
  // =============================================

  /**
   * Get collection list
   * @returns Observable<CollectionGet[]>
   */
  getCollectionList(): Observable<CollectionGet[]> {
    const url = `${this.baseUrl}/Collection/list`; // Use proper casing
    return this.http.get<CollectionGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Create a new collection
   * @param collectionData Collection data to create
   * @returns Observable<string> - Returns the Guid of the created collection
   */
  createCollection(collectionData: CollectionCreate): Observable<string> {
    const url = `${this.baseUrl}/Collection`;
    return this.http.post<string>(url, collectionData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing collection
   * @param collectionData Collection data to update
   * @returns Observable<boolean> - Returns true if the update was successful
   */
  updateCollection(collectionData: CollectionBase): Observable<boolean> {
    const url = `${this.baseUrl}/Collection`;
    return this.http.put<boolean>(url, collectionData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get project list for a specific collection
   * @param collectionId Collection ID to get projects for (optional)
   * @returns Observable<ProjectGet[]>
   */
  getProjectList(collectionId?: string): Observable<ProjectGet[]> {
    let url = `${this.baseUrl}/Project/list`;
    if (collectionId) {
      url += `?collectionId=${collectionId}`;
    }
    return this.http.get<ProjectGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get project list for a specific datasource
   * @param datasourceId Datasource ID to get projects for
   * @returns Observable<ProjectGet[]>
   */
  getProjectListByDatasource(datasourceId: string): Observable<ProjectGet[]> {
    const url = `${this.baseUrl}/Project/list_by_datasource/${datasourceId}`;
    return this.http.get<ProjectGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get unified activity list for projects
   * @param collectionId Optional collection ID to filter activities
   * @returns Observable<UnifiedActivityItem[]>
   */
  getProjectActivityList(collectionId?: string): Observable<UnifiedActivityItem[]> {
    const url = collectionId 
      ? `${this.baseUrl}/User/activity-list?collectionId=${collectionId}`
      : `${this.baseUrl}/User/activity-list`;
    return this.http.get<UnifiedActivityItem[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map the response to camelCase property names and convert date strings
          return items.map(item => ({
            primaryKey: item.PrimaryKey || item.primaryKey,
            name: item.Name || item.name,
            lastActionAt: new Date(item.LastActionAt || item.lastActionAt),
            sourceType: item.SourceType || item.sourceType
          }));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific project by ID
   * @param projectId Project ID to get
   * @returns Observable<ProjectGet>
   */
  getProject(projectId: string): Observable<ProjectGet> {
    const url = `${this.baseUrl}/Project/${projectId}`;
    return this.http.get<ProjectGet>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Create a new project
   * @param projectData Project data to create
   * @returns Observable<number> - Returns the ID of the created project
   */
  createProject(projectData: ProjectCreate): Observable<number> {
    const url = `${this.baseUrl}/Project`;
    return this.http.post<number>(url, projectData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing project
   * @param projectData Project data to update
   * @returns Observable<boolean> - Returns true if the update was successful
   */
  updateProject(projectData: ProjectBase): Observable<boolean> {
    const url = `${this.baseUrl}/Project`;
    return this.http.put<boolean>(url, projectData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Delete a project
   * @param projectId Project ID to delete
   * @returns Observable<boolean> - Returns true if the delete was successful
   */
  deleteProject(projectId: string): Observable<boolean> {
    const url = `${this.baseUrl}/Project/${projectId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Refresh and get project token for API authentication
   * @param projectId Project ID to refresh token for
   * @returns Observable<string> - Returns the refreshed project token (Guid)
   */
  getProjectToken(projectId: string): Observable<string> {
    const url = `${this.baseUrl}/Project/refresh_token/${projectId}`;
    return this.http.put<string>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Clean data for a project using the project token
   * @param projectId Project ID to clean data for
   * @param datasourceId Data source ID to use
   * @param projectToken Project-specific authentication token
   * @returns Observable<string> - Returns a token for polling the long-running action status
   */
  cleanData(projectId: string, datasourceId: string, projectToken: string): Observable<string> {
    const url = `${this.baseUrl}/Project/clean_data/${projectId}/${datasourceId}`;
    
    // Create custom HTTP options with project token instead of user token
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${projectToken}`
      })
    };
    
    return this.http.post<any>(url, {}, { ...httpOptions, responseType: 'text' as 'json' })
      .pipe(
        map((response: any) => {
          // Response shape: {"token":"...","message":"..."}
          const parsed = typeof response === 'string' ? JSON.parse(response) : response;
          return parsed.token as string;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Suggest columns for goals based on datasource schema
   * @param request Request containing goals and schema data
   * @returns Observable<SuggestColumnsResponse> - Returns suggested columns and parameters for each goal
   */
  suggestColumns(request: SuggestColumnsRequest): Observable<SuggestColumnsResponse> {
    const url = `${this.baseUrl}/Project/suggest-columns`;
    return this.http.post<SuggestColumnsResponse>(url, request, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => {
          // Handle case where API returns JSON as a string instead of object
          if (typeof response === 'string') {
            return JSON.parse(response) as SuggestColumnsResponse;
          }
          return response as SuggestColumnsResponse;
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Create a new data source
   * @param dataSourceData Data source data to create
   * @returns Observable<string> - Returns the Guid of the created data source
   */
  createDataSource(dataSourceData: DataSourceCreate): Observable<string> {
    const url = `${this.baseUrl}/Datasource`;
    return this.http.post<string>(url, dataSourceData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing data source
   * @param dataSourceData Data source data to update
   * @returns Observable<boolean> - Returns true if the update was successful
   */
  updateDataSource(dataSourceData: DataSourceUpdate): Observable<boolean> {
    const url = `${this.baseUrl}/Datasource`;
    return this.http.put<boolean>(url, dataSourceData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Delete a data source
   * @param dataSourceId Data source ID to delete
   * @returns Observable<boolean> - Returns true if the delete was successful
   */
  deleteDataSource(dataSourceId: string): Observable<boolean> {
    const url = `${this.baseUrl}/Datasource/${dataSourceId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get data source list
   * @returns Observable<DataSourceGet[]>
   */
  getDataSourceList(): Observable<DataSourceGet[]> {
    const url = `${this.baseUrl}/Datasource/list`;
    return this.http.get<DataSourceGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get the user list
   * @returns Observable<SubscriptionUserListItem[]>
   */
  getUserList(): Observable<SubscriptionUserListItem[]> {
    const url = `${this.baseUrl}/User/List`;
    return this.http.get<SubscriptionUserListItem[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Add a user to the current subscription
   */
  subscribeUser(request: SubscribeUserRequest): Observable<void> {
    const url = `${this.baseUrl}/User/SubscribeUser`;
    return this.http.post<void>(url, request, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Remove a user from the current subscription
   */
  deleteSubscribedUser(userName: string): Observable<void> {
    const url = `${this.baseUrl}/User/subscribeUser/${encodeURIComponent(userName)}`;
    return this.http.delete<void>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific data source by ID
   * @param dataSourceId Data source ID to get
   * @returns Observable<DataSourceGet>
   */
  getDataSource(dataSourceId: string): Observable<DataSourceGet> {
    const url = `${this.baseUrl}/DataSource/${dataSourceId}`;
    return this.http.get<DataSourceGet>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Check if a datasource name already exists
   * @param datasourceName Data source name to check
   * @returns Observable with exists boolean and datasourceName
   */
  checkDataSourceExists(datasourceName: string): Observable<{exists: boolean, dataSourceName: string}> {
    const url = `${this.baseUrl}/DataSource/Exists/${encodeURIComponent(datasourceName)}`;
    return this.http.get<{exists: boolean, dataSourceName: string}>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get data from a datasource
   * @param dataSourceId Data source ID to get data from
   * @returns Observable<any> - Returns the data from the datasource
   */
  getDataSourceData(dataSourceId: string): Observable<any> {
    const url = `${this.baseUrl}/DataSource/data/${dataSourceId}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get specific columns from a datasource
   * @param dataSourceId Data source ID to get data from
   * @param columns Array of column names to fetch
   * @returns Observable<any[]> - Returns array of data rows with only specified columns
   */
  getDataSourceColumns(dataSourceId: string, columns: string[]): Observable<any[]> {
    const columnsParam = columns.join(',');
    const url = `${this.baseUrl}/DataSource/data/${dataSourceId}/columns?Columns=${encodeURIComponent(columnsParam)}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map(response => {
          console.log('Raw response from getDataSourceColumns:', response, 'Type:', typeof response);
          
          // If response is a JSON string, parse it
          if (typeof response === 'string') {
            try {
              response = JSON.parse(response);
            } catch (e) {
              console.error('Failed to parse JSON string response:', e);
              return [];
            }
          }
          
          // Handle response - API may return { data: [...] } or just [...]
          if (Array.isArray(response)) {
            return response;
          } else if (response && Array.isArray(response.data)) {
            return response.data;
          } else if (response && response.Data && Array.isArray(response.Data)) {
            // Handle PascalCase property name
            return response.Data;
          } else {
            console.error('Unexpected response structure from getDataSourceColumns:', response);
            return [];
          }
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Register an API listener data source
   * @param dataSourceData Data source registration data
   * @returns Observable with dataSourceId and dataSourceToken
   */
  registerApiListenerDataSource(dataSourceData: DataSourceRegister): Observable<{dataSourceId: string, dataSourceToken: string}> {
    const url = `${this.baseUrl}/Datasource/apilistener/register`;
    return this.http.post<{dataSourceId: string, dataSourceToken: string}>(url, dataSourceData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Regenerate token for an API listener data source
   * @param dataSourceId Data source ID to regenerate token for
   * @returns Observable with new token as string (Guid)
   */
  regenerateApiListenerToken(dataSourceId: string): Observable<string> {
    const url = `${this.baseUrl}/Datasource/apilistener/token/${dataSourceId}`;
    return this.http.put<string>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Upload a file as a data source
   * @param fileUploadData File upload data including base64-encoded content
   * @returns Observable<any> - Returns the created data source ID (as string) or object with dataSourceId
   */
  uploadFileDataSource(fileUploadData: FileUploadRequest): Observable<any> {
    const url = `${this.baseUrl}/Datasource/uploadfiles/upload`;
    return this.http.post<any>(url, fileUploadData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Upload a file as a data source with progress tracking using multipart/form-data
   * @param file The file to upload
   * @param dataSourceName Name for the data source
   * @param tags Optional comma-separated tags
   * @returns Observable<HttpEvent<string>> - Returns HTTP events including upload progress
   */
  uploadFileDataSourceWithProgress(file: File, dataSourceName: string, tags?: string): Observable<HttpEvent<string>> {
    const url = `${this.baseUrl}/Datasource/uploadfiles/upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dataSourceName', dataSourceName);
    if (tags) {
      formData.append('tags', tags);
    }
    return this.http.post(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text'
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Replace/update an uploaded file data source
   * @param dataSourceId Data source ID to replace
   * @param fileReplaceData File upload replace data with base64-encoded content and filename
   * @returns Observable<FileUploadUpdateResult> - Returns the result of the update operation
   */
  replaceFileDataSource(dataSourceId: string, fileReplaceData: FileUploadReplaceRequest): Observable<FileUploadUpdateResult> {
    const url = `${this.baseUrl}/Datasource/uploadfiles/replace/${dataSourceId}`;
    return this.http.put<FileUploadUpdateResult>(url, fileReplaceData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Replace/update an uploaded file data source with progress tracking using multipart/form-data
   * @param dataSourceId Data source ID to replace
   * @param file The new file to upload
   * @returns Observable<HttpEvent<FileUploadUpdateResult>> - Returns HTTP events including upload progress
   */
  replaceFileDataSourceWithProgress(dataSourceId: string, file: File): Observable<HttpEvent<any>> {
    const url = `${this.baseUrl}/Datasource/uploadfiles/replace/${dataSourceId}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text'
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get schema information from an uploaded file
   * @param schemaRequest File upload schema request with base64-encoded content and filename
   * @returns Observable<FileUploadSchemaResponse> - Returns schema information including columns and row count
   */
  getFileUploadSchema(schemaRequest: FileUploadSchemaRequest): Observable<FileUploadSchemaResponse> {
    const url = `${this.baseUrl}/Datasource/uploadfiles/getschema`;
    return this.http.post<FileUploadSchemaResponse>(url, schemaRequest, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Assign a data source to a project
   * @param projectId Project ID (Guid)
   * @param dataSourceId Data source ID (Guid) - must be a string
   * @returns Observable<boolean> - Returns true if the assignment was successful
   */
  assignDataSourceToProject(projectId: string, dataSourceId: string | any): Observable<boolean> {
    // Safeguard: ensure dataSourceId is a string, extract if it's an object or JSON string
    console.log('assignDataSourceToProject called with:', { projectId, dataSourceId, type: typeof dataSourceId });
    
    let cleanDataSourceId: string;
    if (typeof dataSourceId === 'string') {
      // Check if it's a JSON string (starts with { or [)
      if (dataSourceId.trim().startsWith('{') || dataSourceId.trim().startsWith('[')) {
        console.error('❌ assignDataSourceToProject received a JSON STRING instead of a GUID:', dataSourceId);
        try {
          const parsed = JSON.parse(dataSourceId);
          cleanDataSourceId = parsed.dataSourceId;
          if (!cleanDataSourceId || typeof cleanDataSourceId !== 'string') {
            console.error('❌ Parsed object does not contain valid dataSourceId:', parsed);
            throw new Error('Invalid dataSourceId: JSON string does not contain valid dataSourceId property');
          }
          console.log('✅ Extracted dataSourceId from JSON string:', cleanDataSourceId);
        } catch (e) {
          console.error('❌ Failed to parse JSON string:', e);
          throw new Error('Invalid dataSourceId: expected GUID string, received unparseable JSON string');
        }
      } else {
        cleanDataSourceId = dataSourceId;
      }
    } else if (dataSourceId && typeof dataSourceId === 'object') {
      console.error('❌ assignDataSourceToProject received an OBJECT instead of a string ID:', dataSourceId);
      // Extract the actual ID from the object
      cleanDataSourceId = (dataSourceId as any).dataSourceId;
      if (!cleanDataSourceId || typeof cleanDataSourceId !== 'string') {
        console.error('❌ Failed to extract dataSourceId from object, object structure:', Object.keys(dataSourceId));
        throw new Error('Invalid dataSourceId: expected string, received object without valid dataSourceId property');
      }
      console.log('✅ Extracted dataSourceId from object:', cleanDataSourceId);
    } else {
      console.warn('assignDataSourceToProject received unexpected type, converting to string:', dataSourceId);
      cleanDataSourceId = String(dataSourceId);
    }

    console.log('Final dataSourceId to use in URL:', cleanDataSourceId);
    const url = `${this.baseUrl}/Project/assign/${projectId}/${cleanDataSourceId}`;
    console.log('Full URL:', url);
    
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Post data profile to get profile analysis
   * @param jsonData JSON data to analyze (optional if dataSourceId is provided)
   * @param dataSourceId Optional data source ID to profile
   * @param collectionId Optional collection ID to profile
   * @param name Optional name for the profile
   * @returns Observable<DataProfileResponse> - Returns the profile data analysis
   */
  postDataProfile(
    jsonData?: any,
    dataSourceIds?: string | string[],
    collectionId?: string,
    name?: string,
    steps?: any,
    files: File[] = []
  ): Observable<string> {
    // Build URL with query parameters
    let url = `${this.baseUrl}/DataProfile/profile-data`;
    const queryParams: string[] = [];
    
    for (const dataSourceId of this.toArray(dataSourceIds)) {
      queryParams.push(`datasourceId=${encodeURIComponent(dataSourceId)}`);
    }
    if (collectionId) {
      queryParams.push(`collectionId=${encodeURIComponent(collectionId)}`);
    }
    if (name) {
      queryParams.push(`name=${encodeURIComponent(name)}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    // Use FormData to ensure multipart/form-data content type
    const formData = new FormData();
    for (const file of files) {
      formData.append('Files', file, file.name);
    }
    if (steps !== undefined) {
      formData.append('Steps', JSON.stringify(steps));
    }
    
    return this.http.post<any>(url, formData, { responseType: 'text' as 'json' })
      .pipe(
        map((response: any) => {
          // Response shape: {"token":"...","message":"..."}
          const parsed = typeof response === 'string' ? JSON.parse(response) : response;
          return parsed.token as string;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Profile data from an uploaded file with progress tracking using multipart/form-data
   * @param file File to profile (CSV or JSON) (optional if datasourceId is provided)
   * @param datasourceId Optional datasource ID to profile
   * @param collectionId Optional collection ID
   * @param name Optional profile name
   * @returns Observable<HttpEvent<string>> - Returns HTTP events including upload progress, then resolves to action token
   */
  postDataProfileWithProgress(
    files?: File | File[],
    datasourceIds?: string | string[],
    collectionId?: string,
    name?: string,
    steps?: any
  ): Observable<HttpEvent<string>> {
    let url = `${this.baseUrl}/DataProfile/profile-data`;
    const queryParams: string[] = [];
    
    for (const datasourceId of this.toArray(datasourceIds)) {
      queryParams.push(`datasourceId=${encodeURIComponent(datasourceId)}`);
    }
    if (collectionId) {
      queryParams.push(`collectionId=${encodeURIComponent(collectionId)}`);
    }
    if (name) {
      queryParams.push(`name=${encodeURIComponent(name)}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const formData = new FormData();
    for (const file of this.toArray(files)) {
      formData.append('Files', file, file.name);
    }
    if (steps !== undefined) {
      formData.append('Steps', JSON.stringify(steps));
    }
    
    return this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text' as 'json'
    }).pipe(
      map((event: any) => {
        if (event.type === HttpEventType.Response) {
          const parsed = typeof event.body === 'string' ? JSON.parse(event.body) : event.body;
          return new HttpResponse({ body: parsed.token as string, status: event.status });
        }
        return event;
      }),
      catchError(this.handleError)
    );
  }

  private toArray<T>(value?: T | T[]): T[] {
    if (value === undefined || value === null) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  /**
   * Get existing data profile for a data source
   * @param dataSourceId Data source ID (Guid)
   * @returns Observable<DataProfileGet> - Returns the stored profile data
   */
  getDataProfile(dataSourceId: string): Observable<DataProfileGet> {
    const url = `${this.baseUrl}/DataProfile/profile-data/${dataSourceId}`;
    return this.http.get<DataProfileGet>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get filtered data from a datasource based on filter criteria
   * @param dataSourceId Data source ID (Guid)
   * @param filter Filter criteria with column, operator, and value
   * @returns Observable<any[]> - Returns array of filtered data rows
   */
  getFilteredData(
    dataSourceId: string,
    filter: {
      column: string;
      operator: 'equals' | 'between' | 'in' | 'greater_than' | 'less_than';
      value: any;
    }
  ): Observable<any[]> {
    const url = `${this.baseUrl}/DataSource/filtered-data`;
    
    const requestPayload = {
      DataSourceId: dataSourceId,
      Column: filter.column,
      Operator: filter.operator,
      Value: filter.value
    };

    return this.http.post<any[]>(url, requestPayload, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get filtered data from a datasource using range filters (for rectangle selection)
   * @param dataSourceId Data source ID (Guid)
   * @param filters Array of range filters (column, startValue, endValue)
   * @returns Observable<any[]> - Returns array of filtered data rows
   */
  getDataByRangeFilters(
    dataSourceId: string,
    filters: Array<{ column: string; startValue: string; endValue: string }>
  ): Observable<any[]> {
    const url = `${this.baseUrl}/DataSource/data/${dataSourceId}/filter`;
    
    // Convert to Pascal case for C# backend
    const requestPayload = filters.map(f => ({
      Column: f.column,
      StartValue: f.startValue,
      EndValue: f.endValue
    }));

    return this.http.post<any>(url, requestPayload, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => {
          // Handle case where API returns a JSON string instead of parsed object
          let data = response;
          if (typeof response === 'string') {
            try {
              data = JSON.parse(response);
              console.log('Parsed JSON string response:', data);
            } catch (e) {
              console.error('Failed to parse response string:', e);
              return [];
            }
          }
          
          // Ensure we have an array
          if (Array.isArray(data)) {
            return data;
          }
          
          console.warn('Unexpected response format:', data);
          return [];
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get goal list for a specific project
   * @param projectId Project ID to get goals for
   * @returns Observable<GoalGet[]>
   */
  getGoalList(projectId: string): Observable<GoalGet[]> {
    const url = `${this.baseUrl}/Goal/list/${projectId}`;
    return this.http.get<GoalGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get catalog of available goals that users can choose from
   * @returns Observable<GoalCatalogGet[]>
   */
  getGoalCatalog(): Observable<GoalCatalogGet[]> {
    const url = `${this.baseUrl}/User/goals`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          return items.map(response => ({
            goalId: response.GoalId || response.goalId,
            goalKey: response.GoalKey || response.goalKey,
            name: response.Name || response.name,
            description: response.Description || response.description || null,
            simpleDescription: response.SimpleDescription || response.simpleDescription,
            advancedDescription: response.AdvancedDescription || response.advancedDescription,
            category: response.Category || response.category || null,
            complexity: response.Complexity ?? response.complexity ?? null,
            tags: response.Tags || response.tags || null,
            isActive: response.IsActive ?? response.isActive ?? false,
            isFeatured: response.IsFeatured ?? response.isFeatured,
            isAvailable: response.IsAvailable ?? response.isAvailable ?? true,
            createdAt: response.CreatedAt || response.createdAt,
            updatedAt: response.UpdatedAt || response.updatedAt || null,
            sortOrder: response.SortOrder ?? response.sortOrder ?? null,
            parameters: response.Parameters || response.parameters || null
          } as GoalCatalogGet));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get the assessment options available to the current user.
   */
  getProfilingSteps(): Observable<ProfilingStepOption[]> {
    const url = `${this.baseUrl}/user/profilingSteps`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map(response => {
          const items = Array.isArray(response)
            ? response
            : response?.profilingSteps || response?.ProfilingSteps || response?.items || [];

          return items
            .map((item: any) => ({
              id: item.stepKey || item.StepKey,
              label: item.label || item.Label || item.name || item.Name || item.stepName || item.StepName,
              simpleDescription: item.simpleDescription || item.SimpleDescription || item.description || item.Description || '',
              advancedDescription: item.advancedDescription || item.AdvancedDescription || item.description || item.Description || '',
              isDefault: item.isDefault ?? item.IsDefault ?? item.default ?? item.Default ?? false,
              isAvailable: item.isAvailable ?? item.IsAvailable ?? true
            }))
            .filter((item: ProfilingStepOption & { isAvailable: boolean }) =>
              item.isAvailable && !!item.id && !!item.label
            );
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Create a new goal
   * @param goalData Goal data to create
   * @returns Observable<string> - Returns the Guid of the created goal
   */
  createGoal(goalData: GoalCreate): Observable<string> {
    const url = `${this.baseUrl}/Goal`;
    return this.http.post<string>(url, goalData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing goal
   * @param goalData Goal data to update
   * @returns Observable<boolean> - Returns true if the update was successful
   */
  updateGoal(goalData: GoalUpdate): Observable<boolean> {
    const url = `${this.baseUrl}/Goal`;
    return this.http.put<boolean>(url, goalData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Delete an existing goal
   * @param goalId Goal ID to delete
   * @returns Observable<boolean> - Returns true if the deletion was successful
   */
  deleteGoal(goalId: string): Observable<boolean> {
    const url = `${this.baseUrl}/Goal/${goalId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get task list for a specific goal
   * @param goalId Goal ID to get tasks for
   * @returns Observable<TaskGet[]>
   */
  getTaskList(goalId: string): Observable<TaskGet[]> {
    const url = `${this.baseUrl}/Task/list/${goalId}`;
    return this.http.get<TaskGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get starter tasks with parsed metadata
   * @returns Observable<StarterTasksResponse>
   */
  getStarterTasks(): Observable<StarterTasksResponse> {
    const url = `${this.baseUrl}/StarterTasks`;
    return this.http.get<string>(url, this.getAuthHttpOptions())
      .pipe(
        map(response => {
          // The response is a JSON string, so we need to parse it first
          const taskTemplates: any[] = JSON.parse(response);
          
          // Convert each TaskTemplate to TaskStarter by parsing the TaskMetadata string
          return taskTemplates.map(template => ({
            TaskName: template.TaskName,
            TaskMetadata: JSON.parse(template.TaskMetadata)
          } as TaskStarter));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get tasks for a specific goal
   * @param goalId Goal ID to get tasks for
   * @returns Observable<TaskCatalogGet[]>
   */
  getTasksForGoal(goalId: string): Observable<TaskCatalogGet[]> {
    const url = `${this.baseUrl}/User/${goalId}/tasks`;
    return this.http.get<TaskCatalogGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get output list for a specific project (FileTracker API)
   * @param projectId Project ID to get outputs for (optional - if not provided, gets all outputs)
   * @param fromDate Optional from date filter
   * @returns Observable<OutputGet[]>
   */
  getOutputList(projectId?: string, fromDate?: string): Observable<OutputGet[]> {
    let url = `${this.baseUrl}/FileTracker/list`;
    const params: string[] = [];
    
    if (projectId) {
      params.push(`projectId=${projectId}`);
    }
    if (fromDate) {
      params.push(`fromDate=${fromDate}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    return this.http.get<OutputGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Download an output file (FileTracker API)
   * @param projectId Project ID
   * @param fileName File name to download
   * @param blobPath Optional blob path
   * @returns Observable with file data (format, fileName, contentType, contentBase64)
   */
  downloadOutput(projectId: string, fileName: string, blobPath?: string): Observable<{
    format: string;
    fileName: string;
    contentType: string;
    contentBase64: string;
  }> {
    const url = `${this.baseUrl}/FileTracker/download`;
    const requestBody = {
      ProjectId: projectId,
      FileName: fileName,
      BlobPath: blobPath || null
    };
    return this.http.post<{
      format: string;
      fileName: string;
      contentType: string;
      contentBase64: string;
    }>(url, requestBody, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Create a new data destination for a project
   * @param destinationData Data destination data to create
   * @returns Observable<string> - Returns the Guid of the created data destination
   */
  createDataDestination(destinationData: DataDestinationAddRequest): Observable<string> {
    const url = `${this.baseUrl}/Project/data-destinations`;
    return this.http.post<string>(url, destinationData, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get data destinations for a specific project
   * @param projectId Project ID to get data destinations for
   * @returns Observable<DataDestinationGet[]>
   */
  getDataDestinations(projectId: string): Observable<DataDestinationGet[]> {
    const url = `${this.baseUrl}/Project/${projectId}/data-destinations/list`;
    return this.http.get<DataDestinationGet[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Delete a data destination
   * @param dataDestinationId Data destination ID to delete
   * @returns Observable<boolean> - Returns true if the deletion was successful
   */
  deleteDataDestination(dataDestinationId: string): Observable<boolean> {
    const url = `${this.baseUrl}/Project/data-destinations/${dataDestinationId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Add a task to favorites
   * @param taskName Task name to add to favorites
   * @returns Observable<void>
   */
  addFavoriteTask(taskName: string): Observable<void> {
    const url = `${this.baseUrl}/FavoriteTasks`;
    const request: FavoriteTaskRequest = { taskName };
    return this.http.post<void>(url, request, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Remove a task from favorites
   * @param taskName Task name to remove from favorites
   * @returns Observable<void>
   */
  removeFavoriteTask(taskName: string): Observable<void> {
    const url = `${this.baseUrl}/FavoriteTasks/${encodeURIComponent(taskName)}`;
    return this.http.delete<void>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get list of favorite tasks
   * @returns Observable<string[]> - Returns array of favorite task names
   */
  getFavoriteTasks(): Observable<string[]> {
    const url = `${this.baseUrl}/FavoriteTasks`;
    return this.http.get<string[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Start a project pipeline
   * @param projectId Project ID to start
   * @returns Observable<boolean> - Returns true if the project was started successfully
   */
  startProject(projectId: string): Observable<boolean> {
    const url = `${this.baseUrl}/Project/${projectId}/start`;
    return this.http.post<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get available anomaly detection methods
   * @returns Observable<AdMethod[]> - Returns list of available anomaly detection methods
   */
  getAdMethods(): Observable<AdMethod[]> {
    const url = `${this.baseUrl}/User/adMethods`;
    return this.http.get<AdMethod[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get available anomaly detection methods from catalog
   * @returns Observable<AdMethodCatalogGet[]> - Returns list of available AD methods with full details
   */
  getAvailableAdMethods(): Observable<AdMethodCatalogGet[]> {
    const url = `${this.baseUrl}/User/adMethods`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          return items.map(response => ({
            methodId: response.MethodId || response.methodId,
            methodKey: response.MethodKey || response.methodKey,
            name: response.Name || response.name,
            description: response.Description || response.description,
            simpleDescription: response.SimpleDescription || response.simpleDescription,
            advancedDescription: response.AdvancedDescription || response.advancedDescription,
            complexity: response.Complexity ?? response.complexity ?? null,
            family: response.Family || response.family || null,
            paramsJson: response.ParamsJson || response.paramsJson || null,
            inputsJson: response.InputsJson || response.inputsJson || null,
            isActive: response.IsActive ?? response.isActive ?? true,
            isAvailable: response.IsAvailable ?? response.isAvailable ?? true,
            createdAt: response.CreatedAt || response.createdAt,
            updatedAt: response.UpdatedAt || response.updatedAt || null,
            planIds: response.PlanIds || response.planIds || []
          } as AdMethodCatalogGet));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Perform anomaly detection on provided data
   * @param request Anomaly detection request with data, method ID, and parameters
   * @returns Observable<AnomalyDetectionResponse> - Returns the anomaly detection results
   */
  performAnomalyDetection(request: AnomalyDetectionRequest): Observable<string> {
    const url = `${this.baseUrl}/anomalyDetection`;
    const options = { ...this.getAuthHttpOptions(), responseType: 'text' as 'json' };
    return this.http.post<any>(url, request, options)
      .pipe(
        map((response: any) => {
          // Response shape: {"token":"...","message":"..."}
          const parsed = typeof response === 'string' ? JSON.parse(response) : response;
          return parsed.token as string;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Perform anomaly detection on an uploaded file with progress tracking using multipart/form-data
   * @param file File to analyze (CSV or JSON)
   * @param methodId Detection method ID
   * @param params Method-specific parameters
   * @returns Observable<HttpEvent<string>> - Returns HTTP events including upload progress, then resolves to action token
   */
  performAnomalyDetectionWithProgress(file: File, methodId: string, params: Record<string, any>, name?: string): Observable<HttpEvent<string>> {
    let url = `${this.baseUrl}/anomalyDetection`;
    if (name) {
      url += `?name=${encodeURIComponent(name)}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('methodId', methodId);
    formData.append('parameters', JSON.stringify(params));
    return this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text' as 'json'
    }).pipe(
      map((event: any) => {
        if (event.type === HttpEventType.Response) {
          const parsed = typeof event.body === 'string' ? JSON.parse(event.body) : event.body;
          return new HttpResponse({ body: parsed.token as string, status: event.status });
        }
        return event;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete cleaning results for a project by result ID
   * @param cleanResultId Clean result ID (Guid)
   * @returns Observable<boolean> - Returns true if delete was successful
   */
  deleteCleanResult(projectId: string, fileName: string, blobPath: string): Observable<boolean> {
    const url = `${this.baseUrl}/FileTracker/delete`;
    const requestBody = {
      ProjectId: projectId,
      FileName: fileName,
      BlobPath: blobPath || null
    };

    return this.http.post<boolean>(url, requestBody, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Perform anomaly detection on an existing datasource with progress tracking using multipart/form-data
   * @param datasourceGuid Datasource GUID to perform anomaly detection on
   * @param request Anomaly detection datasource request with method ID and parameters
   * @param collectionId Optional collection ID to perform anomaly detection on
   * @param name Optional name for the detection run
   * @returns Observable<HttpEvent<string>> - Returns HTTP events including progress, then resolves to action token
   */
  performAnomalyDetectionOnDatasource(
    datasourceGuid: string,
    request: AnomalyDetectionDatasourceRequest,
    collectionId?: string,
    name?: string
  ): Observable<HttpEvent<string>> {
    let url = `${this.baseUrl}/anomalyDetection`;
    const queryParams: string[] = [];
    
    queryParams.push(`datasourceId=${encodeURIComponent(datasourceGuid)}`);
    if (collectionId) {
      queryParams.push(`collectionId=${encodeURIComponent(collectionId)}`);
    }
    if (name) {
      queryParams.push(`name=${encodeURIComponent(name)}`);
    }
    
    url += `?${queryParams.join('&')}`;
    
    const formData = new FormData();
    formData.append('methodId', request.MethodId);
    formData.append('parameters', JSON.stringify(request.Params));
    
    return this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text' as 'json'
    }).pipe(
      map((event: any) => {
        if (event.type === HttpEventType.Response) {
          const parsed = typeof event.body === 'string' ? JSON.parse(event.body) : event.body;
          return new HttpResponse({ body: parsed.token as string, status: event.status });
        }
        return event;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get a single anomaly detection run by ID
   * @param runId Anomaly detection run ID (Guid)
   * @returns Observable<AnomalyDetectionRunGet> - Returns the anomaly detection run details
   */
  getAnomalyDetectionRun(runId: string): Observable<AnomalyDetectionRunGet> {
    const url = `${this.baseUrl}/anomalyDetection/${runId}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return {
            anomalyDetectionRunId: response.AnomalyDetectionRunId || response.anomalyDetectionRunId,
            dataSourceId: response.DataSourceId || response.dataSourceId,
            methodKey: response.MethodKey || response.methodKey,
            name: response.Name ?? response.name ?? null,
            resultJson: response.ResultJson || response.resultJson,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: new Date(response.UpdatedAt || response.updatedAt)
          } as AnomalyDetectionRunGet;
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get list of anomaly detection runs
   * @returns Observable<AnomalyDetectionRunListItem[]> - Returns list of anomaly detection runs
   */
  getAnomalyDetectionRunList(): Observable<AnomalyDetectionRunListItem[]> {
    const url = `${this.baseUrl}/anomalyDetection/list`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return items.map(response => ({
            anomalyDetectionRunId: response.AnomalyDetectionRunId || response.anomalyDetectionRunId,
            collectionId: response.CollectionId || response.collectionId,
            dataSourceId: response.DataSourceId || response.dataSourceId,
            dataSourceName: response.DataSourceName || response.dataSourceName,
            methodKey: response.MethodKey || response.methodKey,
            name: response.Name ?? response.name ?? null,
            resultJson: response.ResultJson || response.resultJson,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: new Date(response.UpdatedAt || response.updatedAt)
          } as AnomalyDetectionRunListItem));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get list of data profiles
   * @returns Observable<DataProfileListItem[]> - Returns list of data profiles
   */
  getDataProfileList(): Observable<DataProfileListItem[]> {
    const url = `${this.baseUrl}/DataProfile/profile-data/list`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return items.map(response => ({
            dataProfileRunId: response.DataProfileRunId || response.dataProfileRunId,
            collectionId: response.CollectionId || response.collectionId,
            dataSourceId: response.DataSourceId || response.dataSourceId,
            dataSourceName: response.DataSourceName || response.dataSourceName,
            name: response.Name || response.name,
            profileJson: response.ProfileJson || response.profileJson,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: new Date(response.UpdatedAt || response.updatedAt)
          } as DataProfileListItem));
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get the current user's plan
   * @returns Observable<UserPlan> - Returns the user's current subscription plan
   */
  getUserPlan(): Observable<UserPlan> {
    const url = `${this.baseUrl}/User/plan`;
    return this.http.get<string>(url, this.getAuthHttpOptions())
      .pipe(
        map(response => {
          // The response is a JSON string, so we need to parse it
          if (typeof response === 'string') {
            return JSON.parse(response) as UserPlan;
          }
          return response as UserPlan;
        }),
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Get available plans for the user's region
   * @returns Observable<AvailablePlan[]> - Returns list of available plans
   */
  getAvailablePlans(): Observable<AvailablePlan[]> {
    const url = `${this.baseUrl}/User/available-plans`;
    return this.http.get<AvailablePlan[]>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Resolve Azure Marketplace subscription token
   * Converts the marketplace token into subscription details
   * @param token Token from Azure Marketplace redirect URL
   * @returns Observable<MarketplaceResolveResponse> - Returns subscription details
   */
  resolveMarketplaceSubscription(token: string): Observable<MarketplaceResolveResponse> {
    const url = `${this.baseUrl}/marketplace/resolve`;
    const request: MarketplaceResolveRequest = { token };
    return this.http.post<MarketplaceResolveResponse>(url, request, this.httpOptions)
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Activate Azure Marketplace subscription
   * Links the marketplace subscription to the authenticated user and activates it
   * @param request Marketplace activation request with subscription details
   * @returns Observable<MarketplaceActivateResponse> - Returns activation result
   */
  activateMarketplaceSubscription(request: MarketplaceActivateRequest): Observable<MarketplaceActivateResponse> {
    const url = `${this.baseUrl}/marketplace/activate`;
    return this.http.post<MarketplaceActivateResponse>(url, request, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Logout the current user
   * @returns Observable<void> - Returns void on successful logout
   */
  logoutUser(): Observable<void> {
    const url = `${this.baseUrl}/User/logout`;
    return this.http.get<void>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Health check endpoint to verify API connectivity
   * @returns Observable with health check response
   */
  healthCheck(): Observable<{ message: string; response?: any; error?: any }> {
    const url = `${this.baseUrl}/Health`;
    return this.http.get<{ message: string; response?: any; error?: any }>(url, this.getAuthHttpOptions())
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get the base URL for API endpoints
   * @returns The base URL string
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get HTTP options with authentication headers
   * @returns HTTP options object with Bearer token
   */
  private getAuthHttpOptions(): { headers: HttpHeaders } {
    const token = this.authService.getToken();
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      })
    };
  }

  /**
   * Update the collection for a data source
   * @param dataSourceId Data source ID (Guid)
   * @param collectionId New collection ID (Guid)
   * @returns Observable<boolean> - Returns true if update was successful
   */
  updateDataSourceCollection(dataSourceId: string, collectionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/datasource/UpdateCollection/${dataSourceId}/${collectionId}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Update the collection for an anomaly detection run
   * @param anomalyDetectionRunId Anomaly detection run ID (Guid)
   * @param collectionId New collection ID (Guid)
   * @returns Observable<boolean> - Returns true if update was successful
   */
  updateAnomalyDetectionCollection(anomalyDetectionRunId: string, collectionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/anomalydetection/UpdateCollection/${anomalyDetectionRunId}/${collectionId}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Rename an anomaly detection run
   * @param anomalyDetectionRunId Anomaly detection run ID (Guid)
   * @param name New name for the run
   * @returns Observable<boolean> - Returns true if rename was successful
   */
  renameAnomalyDetectionRun(anomalyDetectionRunId: string, name: string): Observable<boolean> {
    const url = `${this.baseUrl}/anomalydetection/${anomalyDetectionRunId}/rename?name=${encodeURIComponent(name)}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Update the collection for a data profile run
   * @param dataProfileRunId Data profile run ID (Guid)
   * @param collectionId New collection ID (Guid)
   * @returns Observable<boolean> - Returns true if update was successful
   */
  updateDataProfileCollection(dataProfileRunId: string, collectionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/dataprofile/UpdateCollection/${dataProfileRunId}/${collectionId}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Rename a data profile run
   * @param dataProfileRunId Data profile run ID (Guid)
   * @param name New name for the run
   * @returns Observable<boolean> - Returns true if rename was successful
   */
  renameDataProfileRun(dataProfileRunId: string, name: string): Observable<boolean> {
    const url = `${this.baseUrl}/dataprofile/${dataProfileRunId}/rename?name=${encodeURIComponent(name)}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Delete an anomaly detection run
   * @param anomalyDetectionRunId Anomaly detection run ID (Guid)
   * @returns Observable<boolean> - Returns true if delete was successful
   */
  deleteAnomalyDetectionRun(anomalyDetectionRunId: string): Observable<boolean> {
    const url = `${this.baseUrl}/anomalyDetection/${anomalyDetectionRunId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Delete a data profile (data assessment) run
   * @param dataProfileRunId Data profile run ID (Guid)
   * @returns Observable<boolean> - Returns true if delete was successful
   */
  deleteDataProfileRun(dataProfileRunId: string): Observable<boolean> {
    const url = `${this.baseUrl}/DataProfile/profile-data/${dataProfileRunId}`;
    return this.http.delete<boolean>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Run a hypothesis test
   * @param request Hypothesis testing request with test ID and parameters
   * @param collectionId Optional collection ID to associate the run with
   * @param saveResult Whether to save the result (default: true)
   * @returns Observable<any> - Returns the hypothesis test results
   */
  runHypothesisTest(request: HypothesisTestingRequest, collectionId?: string, saveResult: boolean = true): Observable<any> {
    let url = `${this.baseUrl}/hypothesisTesting`;
    if (collectionId) {
      url += `/${collectionId}`;
    }
    url += `?saveResult=${saveResult}`;
    
    const options = { ...this.getAuthHttpOptions(), responseType: 'text' as 'json' };
    return this.http.post(url, request, options)
      .pipe(
        map((response: any) => {
          // API may return a JSON string
          let parsed = response;
          while (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed);
            } catch {
              break;
            }
          }
          return parsed;
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get a single hypothesis testing run by ID
   * @param runId Hypothesis testing run ID (Guid)
   * @returns Observable<HypothesisTestingRunGet> - Returns the hypothesis testing run details
   */
  getHypothesisTestingRun(runId: string): Observable<HypothesisTestingRunGet> {
    const url = `${this.baseUrl}/hypothesisTesting/${runId}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return {
            hypothesisTestingRunId: response.HypothesisTestingRunId || response.hypothesisTestingRunId,
            collectionId: response.CollectionId || response.collectionId,
            dataSourceId: response.DataSourceId || response.dataSourceId,
            dataSourceName: response.DataSourceName || response.dataSourceName,
            methodKey: response.MethodKey || response.methodKey,
            resultJson: response.ResultJson || response.resultJson,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: new Date(response.UpdatedAt || response.updatedAt)
          } as HypothesisTestingRunGet;
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get list of hypothesis testing runs
   * @param collectionId Optional collection ID to filter by
   * @returns Observable<HypothesisTestingRunGet[]> - Returns list of hypothesis testing runs
   */
  getHypothesisTestingRunList(collectionId?: string): Observable<HypothesisTestingRunGet[]> {
    let url = `${this.baseUrl}/hypothesisTesting/list`;
    if (collectionId) {
      url += `?collectionId=${collectionId}`;
    }
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return items.map(response => ({
            hypothesisTestingRunId: response.HypothesisTestingRunId || response.hypothesisTestingRunId,
            collectionId: response.CollectionId || response.collectionId,
            dataSourceId: response.DataSourceId || response.dataSourceId,
            dataSourceName: response.DataSourceName || response.dataSourceName,
            methodKey: response.MethodKey || response.methodKey,
            resultJson: response.ResultJson || response.resultJson,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: new Date(response.UpdatedAt || response.updatedAt)
          } as HypothesisTestingRunGet));
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Update the collection for a hypothesis testing run
   * @param hypothesisTestingRunId Hypothesis testing run ID (Guid)
   * @param collectionId New collection ID (Guid)
   * @returns Observable<boolean> - Returns true if update was successful
   */
  updateHypothesisTestingCollection(hypothesisTestingRunId: string, collectionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/hypothesisTesting/UpdateCollection/${hypothesisTestingRunId}/${collectionId}`;
    return this.http.put<boolean>(url, {}, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get hypothesis testing methods available to the user based on their plan
   * @returns Observable<HtMethodCatalogGet[]> - Returns list of hypothesis testing methods
   */
  getUserHTMethods(): Observable<HtMethodCatalogGet[]> {
    const url = `${this.baseUrl}/User/htMethods`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return items.map(response => ({
            methodId: response.MethodId || response.methodId,
            methodKey: response.MethodKey || response.methodKey,
            name: response.Name || response.name,
            description: response.Description || response.description,
            paramsJson: response.ParamsJson || response.paramsJson,
            isActive: response.IsActive ?? response.isActive ?? true,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: response.UpdatedAt || response.updatedAt ? new Date(response.UpdatedAt || response.updatedAt) : undefined,
            planIds: response.PlanIds || response.planIds || []
          } as HtMethodCatalogGet));
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get all available hypothesis testing methods across available plans
   * @returns Observable<HtMethodCatalogGet[]> - Returns list of all available hypothesis testing methods
   */
  getAvailableHTMethods(): Observable<HtMethodCatalogGet[]> {
    const url = `${this.baseUrl}/User/available-htMethods`;
    return this.http.get<any[]>(url, this.getAuthHttpOptions())
      .pipe(
        map((items: any[]) => {
          // Map API response (PascalCase) to camelCase and convert dates
          return items.map(response => ({
            methodId: response.MethodId || response.methodId,
            methodKey: response.MethodKey || response.methodKey,
            name: response.Name || response.name,
            description: response.Description || response.description,
            paramsJson: response.ParamsJson || response.paramsJson,
            isActive: response.IsActive ?? response.isActive ?? true,
            createdAt: new Date(response.CreatedAt || response.createdAt),
            updatedAt: response.UpdatedAt || response.updatedAt ? new Date(response.UpdatedAt || response.updatedAt) : undefined,
            planIds: response.PlanIds || response.planIds || []
          } as HtMethodCatalogGet));
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get user settings
   * @returns Observable<UserSettings> - Returns the user's settings
   */
  getUserSettings(): Observable<UserSettings> {
    const url = `${this.baseUrl}/User/Settings`;
    return this.http.get<UserSettings>(url, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Update user settings
   * @param settings User settings to update
   * @returns Observable<boolean> - Returns true if update was successful
   */
  updateUserSettings(settings: UserSettings): Observable<boolean> {
    const url = `${this.baseUrl}/User/Settings`;
    return this.http.post<boolean>(url, settings, this.getAuthHttpOptions())
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get the status of a long running action
   * @param token Action token (Guid)
   * @returns Observable<LongRunningActionStatus> - Returns the current status of the action
   */
  getLongRunningActionStatus(token: string): Observable<LongRunningActionStatus> {
    const url = `${this.baseUrl}/LongRunningAction/status/${token}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => ({
          token: response.Token || response.token,
          actionType: response.ActionType || response.actionType,
          status: response.Status ?? response.status,
          currentStep: response.CurrentStep ?? response.currentStep,
          totalSteps: response.TotalSteps ?? response.totalSteps,
          goalId: response.GoalId || response.goalId,
          goalName: response.GoalName || response.goalName,
          detailsJson: response.DetailsJson || response.detailsJson,
          errorMessage: response.ErrorMessage || response.errorMessage,
          createdAt: new Date(response.CreatedAt || response.createdAt),
          updatedAt: response.UpdatedAt || response.updatedAt ? new Date(response.UpdatedAt || response.updatedAt) : undefined,
          completedAt: response.CompletedAt || response.completedAt ? new Date(response.CompletedAt || response.completedAt) : undefined
        } as LongRunningActionStatus)),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Get the result of a completed long running action
   * @param token Action token (Guid)
   * @returns Observable<ActionResultGet> - Returns the action result including typed result data
   */
  getLongRunningActionResult(token: string): Observable<ActionResultGet> {
    const url = `${this.baseUrl}/LongRunningAction/result/${token}`;
    return this.http.get<any>(url, this.getAuthHttpOptions())
      .pipe(
        map((response: any) => {
          const profileRaw = response.ProfileResult || response.profileResult;
          const cleanRaw = response.CleanResult || response.cleanResult;
          const anomalyRaw = response.AnomalyResult || response.anomalyResult;
          return {
            token: response.Token || response.token,
            actionType: response.ActionType || response.actionType,
            profileResult: profileRaw ? {
              profileJson: profileRaw.ProfileJson || profileRaw.profileJson,
              metricsJson: profileRaw.MetricsJson || profileRaw.metricsJson
            } : undefined,
            cleanResult: cleanRaw ? {
              resultJson: cleanRaw.ResultJson || cleanRaw.resultJson,
              metricsJson: cleanRaw.MetricsJson || cleanRaw.metricsJson
            } : undefined,
            anomalyResult: anomalyRaw ? {
              resultJson: anomalyRaw.ResultJson || anomalyRaw.resultJson,
              metricsJson: anomalyRaw.MetricsJson || anomalyRaw.metricsJson
            } : undefined
          } as ActionResultGet;
        }),
        retry(1),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   * @param error HttpErrorResponse
   * @returns Observable<never>
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      // Server-side error
      
      // Check if this is a JSON parsing error (happens when server returns plain text but client expects JSON)
      if (error.error instanceof ProgressEvent || (error.message && error.message.includes('is not valid JSON'))) {
        // Try to extract the response text from the error
        // For 402 errors and similar, the server returns plain text which fails JSON parsing
        // The actual message might be in error.error.text or we need to use statusText
        if (error.status === 402 && error.statusText) {
          errorMessage = error.statusText;
        } else if ((error as any).error?.text) {
          errorMessage = (error as any).error.text;
        } else {
          errorMessage = `Server Error: ${error.status} - ${error.statusText || error.message}`;
        }
      } else if (typeof error.error === 'string' && error.error.trim() !== '') {
        // Use the string from the response body as the error message
        errorMessage = error.error;
      } else if (error.error && typeof error.error === 'object' && error.error.message) {
        // If it's an object with a message property, use that
        errorMessage = error.error.message;
      } else {
        // Fall back to default error message with status
        errorMessage = `Server Error: ${error.status} - ${error.message}`;
      }
    }
    
    console.error('HTTP Error:', errorMessage);
    console.error('Full error object:', error);
    
    // Create an error object with the status code preserved
    const customError: any = new Error(errorMessage);
    customError.status = error.status;
    
    return throwError(() => customError);
  }
}
